import { Box, Text } from '@chakra-ui/react'
import { DefaultModal } from '@components/Modals'
import { PracticeProviderSelect } from '@components/Practices/components/PracticeProviderSelect'
import { doNotBillField, Encounter, EncounterType, SubmittedAuthentication } from '@hb/shared'
import { Dispatch, SetStateAction } from 'react'
import { AuthenticateAndSubmit } from '../../../AuthenticateCheckbox'
import { FormElement } from '../../../../components/forms/Input'
import { SupplyLogPreAuthGate } from './SupplyLogPreAuthGate'

interface AuthenticateEncounterModalProps {
  isOpen: boolean
  onClose: () => void
  typeName: string
  encounter: Encounter | null
  examId: string | null
  examType: EncounterType
  practiceId: string | null
  coSignRequired: boolean
  authChecked: boolean
  setAuthChecked: Dispatch<SetStateAction<boolean>>
  onAuthenticate: (auth: SubmittedAuthentication) => Promise<void>
}

export const AuthenticateEncounterModal = ({
  isOpen,
  onClose,
  typeName,
  encounter,
  examId,
  examType,
  practiceId,
  coSignRequired,
  authChecked,
  setAuthChecked,
  onAuthenticate,
}: AuthenticateEncounterModalProps) => (
  <DefaultModal
    isOpen={isOpen}
    onClose={onClose}
    header={`Authenticate ${typeName}`}
    render={({ onClose: closeModal }) => {
      // §6.7: For birthing center encounters, gate on supply log review.
      // §6.9: mobile-clinic uses the same supply log module as birthing center
      const location = (encounter?.data as { visitLocation?: { location?: string } } | undefined)
        ?.visitLocation?.location
      const isBirthingCenter =
        examType === EncounterType.BIRTHING_CENTER_ADMISSION ||
        location === 'birthing-center' ||
        location === 'mobile-clinic'
      const supplyGateActive = isBirthingCenter && encounter && !encounter.supplyLogReviewed

      return (
        <Box>
          <Box px={4} pt={2} pb={1}>
            <FormElement field={doNotBillField} name="data.doNotBill" />
          </Box>
          {/* §2.11: Optional co-signature provider — shown as optional unless P3-A/P3-B gate is active. */}
          {/* §A P3-A: When coSignRequired is true, label becomes Required and a clinical info line appears. */}
          <Box px={4} pb={2}>
            <Text
              fontSize="xs"
              color={coSignRequired ? 'red.500' : 'gray.500'}
              fontWeight={600}
              mb={1}>
              {coSignRequired
                ? 'Co-Signature Provider (Required)'
                : 'Co-Signature Provider (Optional)'}
            </Text>
            {coSignRequired && (
              <Text fontSize="xs" color="gray.500" mb={2}>
                Your provider configuration requires a co-signature before this note can be
                authenticated.
              </Text>
            )}
            <PracticeProviderSelect
              practiceId={practiceId}
              name="data.coSignedBy"
              placeholder="Select co-signing provider..."
              style={{ padding: '0.35rem' }}
              inputStyle={{ fontSize: '0.875rem' }}
            />
          </Box>
          {supplyGateActive && examId ? (
            <SupplyLogPreAuthGate encounter={encounter} examId={examId} />
          ) : (
            <AuthenticateAndSubmit
              authChecked={authChecked}
              onChange={setAuthChecked}
              itemName="encounter"
              practiceId={practiceId}
              onSubmit={async auth => {
                await onAuthenticate(auth)
                closeModal()
              }}
            />
          )}
        </Box>
      )
    }}
  />
)
